import React, { useMemo, useState, useContext, useEffect } from 'react';
import { LanguageContext } from '../contexts/LanguageContext';
import { policiesContent } from '../policiesContent';
import './Policies.css';

const Policies = () => {
  const { language } = useContext(LanguageContext);
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(null);

  const langContent = policiesContent[language] || [];

  // ✅ خريطة سريعة للوصول لمحتوى الإنجليزية حسب id (لا تؤثر على الفهرس)
  const enDocMap = useMemo(() => {
    const map = new Map();
    (policiesContent.en || []).forEach((cat) => {
      (cat.docs || []).forEach((doc) => {
        if (doc?.id) map.set(doc.id, doc);
      });
    });
    return map;
  }, []);

  useEffect(() => {
    if (language === 'ar') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [language]);

  useEffect(() => {
    const onHash = () => {
      const id = window.location.hash?.replace('#', '');
      if (id) setActiveId(id);
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const view = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return langContent;

    return (langContent || [])
      .map((cat) => {
        const docs = (cat.docs || []).filter((doc) => {
          const hay =
            (doc.title + ' ' + (doc.items || []).map((i) => i.text).join(' ')).toLowerCase();
          return hay.includes(q);
        });
        return { ...cat, docs };
      })
      .filter((cat) => (cat.docs || []).length > 0);
  }, [langContent, query]);

  // ✅ Renderer داخلي يعتمد على items مباشرة (عشان نستخدمه للعربي أو الإنجليزي)
  const renderItems = (items) => {
    const out = [];
    let listBuffer = [];

    const flushList = () => {
      if (listBuffer.length) {
        out.push(
          <ul className="policies-ul" key={`ul-${out.length}`}>
            {listBuffer.map((t, idx) => (
              <li key={idx}>{t}</li>
            ))}
          </ul>
        );
        listBuffer = [];
      }
    };

    (items || []).forEach((it, idx) => {
      const type = it.type || 'p';
      const text = it.text || '';

      if (!text.trim()) return;

      if (type === 'li') {
        listBuffer.push(text);
        return;
      }

      flushList();

      if (type === 'heading') {
        const level = Math.min(Math.max(it.level || 2, 2), 4);
        const Tag = `h${level}`;
        out.push(
          <Tag className={`policies-h policies-h${level}`} key={`h-${idx}`}>
            {text}
          </Tag>
        );
        return;
      }

      out.push(
        <p className="policies-p" key={`p-${idx}`}>
          {text}
        </p>
      );
    });

    flushList();
    return out;
  };

  const AR_BOILERPLATE = new Set([
  'بسم الله الرحمن الرحيم',
  'منظمة الخلد للاعمار والتنمية',
  'منظمة الخلد للإعمار والتنمية',
]);

const hasMeaningfulArabic = (doc) => {
  if (!doc?.items || doc.items.length === 0) return false;

  // نعتبر "محتوى عربي" فقط إذا وجدنا نص عربي غير الديباجة وغير فارغ
  return doc.items.some((it) => {
    const t = (it.text || '').trim();
    if (!t) return false;
    if (AR_BOILERPLATE.has(t)) return false;

    // لو كان النص عربي فعلاً (أي يحتوي أحرف عربية)
    return /[\u0600-\u06FF]/.test(t);
  });
};


const renderDocBody = (doc) => {
  if (language === 'ar' && !hasMeaningfulArabic(doc)) {
    const enDoc = enDocMap.get(doc.id);
    if (enDoc?.items?.length) {
      return renderItems(enDoc.items);
    }
  }

  return renderItems(doc.items || []);
};


  const pageTitle = language === 'ar' ? 'السياسات والإجراءات' : 'Policies & Procedures';
  const pageSubtitle =
    language === 'ar'
      ? 'فهرس شامل للسياسات مع إمكانية البحث والتصفح.'
      : 'A complete index of policies with search and easy browsing.';

  return (
    <div className={`policies-page ${language === 'ar' ? 'rtl' : ''}`}>
      <header className="policies-hero">
        <div className="policies-hero-inner">
          <div>
            <h1 className="policies-title">{pageTitle}</h1>
            <p className="policies-subtitle">{pageSubtitle}</p>
          </div>

          <div className="policies-search">
            <input
              className="policies-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={language === 'ar' ? 'ابحث داخل السياسات...' : 'Search policies...'}
            />
            {query && (
              <button className="policies-search-clear" onClick={() => setQuery('')}>
                {language === 'ar' ? 'مسح' : 'Clear'}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="policies-shell">
        {/* ✅ الفهرس كما هو تمامًا (لم ألمسه) */}
        <aside className="policies-aside">
          <div className="policies-aside-card">
            <div className="policies-aside-title">{language === 'ar' ? 'الفهرس' : 'Index'}</div>

            {view.map((cat) => (
              <div className="policies-aside-group" key={cat.slug || cat.title}>
                <div className="policies-aside-group-title">{cat.title}</div>
                <ul className="policies-aside-list">
                  {(cat.docs || []).map((doc) => {
                    const href = `#${doc.id}`;
                    const isActive = activeId === doc.id;
                    return (
                      <li key={doc.id}>
                        <a className={`policies-aside-link ${isActive ? 'active' : ''}`} href={href}>
                          {doc.title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        <main className="policies-main">
          {view.map((cat) => (
            <section className="policies-category" key={cat.slug || cat.title}>
              <div className="policies-category-head">
                <h2 className="policies-category-title">{cat.title}</h2>
                <div className="policies-category-line" />
              </div>

              <div className="policies-grid">
                {(cat.docs || []).map((doc) => (
                  <article className="policies-card" key={doc.id} id={doc.id}>
                    <div className="policies-card-top">
                      <div className="policies-card-title">{doc.title}</div>
                      <a className="policies-card-anchor" href={`#${doc.id}`} title="Permalink">
                        #
                      </a>
                    </div>

                    <details className="policies-details" open>
                      <summary className="policies-summary">
                        {language === 'ar' ? 'عرض المحتوى' : 'View content'}
                      </summary>
                      <div className="policies-body">{renderDocBody(doc)}</div>
                    </details>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default Policies;
